import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { FeedHandle } from '../feed/feed.tsx';
import { useScrollParent } from '../scroll-parent-context';

export const SCROLL_LOCK_DELTA = 180;
export const COMPACT_FEED_SCROLL_LOCK_DELTA = 5;

const PREPEND_OBSERVE_MS = 1500;

type Identifiable = { id: string };

const haveSameItems = <T extends Identifiable>(
  a: readonly T[] | undefined | null,
  b: readonly T[] | undefined | null,
): boolean => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
};

const detectPrependedCount = <T extends Identifiable>(
  prev: readonly T[],
  next: readonly T[],
): number => {
  if (prev.length === 0) return 0;

  const prevIds = new Set<string>();
  for (const item of prev) prevIds.add(item.id);

  let count = 0;
  for (const item of next) {
    if (prevIds.has(item.id)) break;
    count++;
  }
  if (count === 0) return 0;
  // A prepend may not grow the list: at MAX_CACHED_FEED_ITEMS the feed cache
  // swaps one-in-one-out, so validate by head alignment rather than length.
  if (next[count]?.id !== prev[0]?.id) return 0;
  return count;
};

type Anchor = { itemIndex: string; topBefore: number };

const getViewportTop = (target: HTMLElement): number =>
  target === document.scrollingElement ? 0 : target.getBoundingClientRect().top;

/** Scroll distance from the top of the feed, ignoring any banner above it
 *  when the scroll parent is the window. */
const getFeedScrollOffset = (target: HTMLElement): number => {
  const listEl = target.querySelector<HTMLElement>(
    '[data-testid="virtuoso-item-list"]',
  );
  if (!listEl) return target.scrollTop;
  return getViewportTop(target) - listEl.getBoundingClientRect().top;
};

const findFirstVisibleItem = (target: HTMLElement): HTMLElement | null => {
  const listEl = target.querySelector<HTMLElement>(
    '[data-testid="virtuoso-item-list"]',
  );
  if (!listEl) return null;

  const viewportTop = getViewportTop(target);
  const items = listEl.querySelectorAll<HTMLElement>('[data-item-index]');
  for (const el of items) {
    if (el.getBoundingClientRect().bottom > viewportTop) return el;
  }
  return null;
};

const captureAnchor = (target: HTMLElement | null): Anchor | null => {
  if (!target) return null;
  const el = findFirstVisibleItem(target);
  if (!el || el.dataset.itemIndex == null) return null;
  return {
    itemIndex: el.dataset.itemIndex,
    topBefore: el.getBoundingClientRect().top,
  };
};

const findAnchorEl = (
  target: HTMLElement | null,
  anchor: Anchor,
): HTMLElement | null =>
  target?.querySelector<HTMLElement>(
    `[data-testid="virtuoso-item-list"] [data-item-index="${anchor.itemIndex}"]`,
  ) ?? null;

type UseFeedScrollLockArgs<T> = {
  data: readonly T[] | undefined;
  feedRef: RefObject<FeedHandle | null>;
  compactFeed?: boolean;
  lockFeedPosition?: boolean;
  onTotalListHeightChanged?: (height: number) => void;
};

/** Scroll-locks on prepend by re-anchoring `scrollTop` to a captured first-
 *  visible item across Virtuoso's prepend phases and later size changes. */
export const useFeedScrollLock = <T extends Identifiable>({
  data,
  feedRef,
  compactFeed,
  lockFeedPosition,
  onTotalListHeightChanged,
}: UseFeedScrollLockArgs<T>) => {
  const { scrollParent } = useScrollParent();

  const scrollParentRef = useRef(scrollParent);
  scrollParentRef.current = scrollParent;
  const compactFeedRef = useRef(compactFeed);
  compactFeedRef.current = compactFeed;
  const lockFeedPositionRef = useRef(lockFeedPosition);
  lockFeedPositionRef.current = lockFeedPosition;

  const [presentedData, setPresentedData] = useState(data);
  const presentedDataRef = useRef(presentedData);
  presentedDataRef.current = presentedData;

  const unmountedRef = useRef(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeTimeoutRef = useRef<number | null>(null);
  const activeCorrectionRef = useRef<(() => void) | null>(null);
  const activeCorrectionDeadlineRef = useRef(0);

  const stopActiveCorrection = () => {
    activeCorrectionRef.current = null;
    activeCorrectionDeadlineRef.current = 0;
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    if (resizeTimeoutRef.current != null) {
      clearTimeout(resizeTimeoutRef.current);
      resizeTimeoutRef.current = null;
    }
  };

  useEffect(
    () => () => {
      unmountedRef.current = true;
      stopActiveCorrection();
    },
    [],
  );

  const handleTotalListHeightChanged = (height: number) => {
    onTotalListHeightChanged?.(height);
    const fn = activeCorrectionRef.current;
    if (!fn) return;
    if (performance.now() > activeCorrectionDeadlineRef.current) {
      stopActiveCorrection();
      return;
    }
    fn();
  };

  useLayoutEffect(() => {
    const prev = presentedDataRef.current ?? [];
    const next = data ?? [];

    if (haveSameItems(prev, next)) return;

    const prependedCount = detectPrependedCount(prev, next);

    if (prependedCount === 0) {
      setPresentedData(data);
      return;
    }

    const target = scrollParentRef.current;
    if (!target) {
      setPresentedData(data);
      return;
    }

    const threshold = compactFeedRef.current
      ? COMPACT_FEED_SCROLL_LOCK_DELTA
      : SCROLL_LOCK_DELTA;
    const isScrolledAway = getFeedScrollOffset(target) > threshold;
    const shouldCompensate = isScrolledAway || !!lockFeedPositionRef.current;

    if (!shouldCompensate) {
      setPresentedData(data);
      return;
    }

    const anchor = captureAnchor(target);
    if (!anchor) {
      setPresentedData(data);
      feedRef.current?.prepend((p) => p + prependedCount);
      return;
    }

    queueMicrotask(() => {
      if (unmountedRef.current) return;
      const t = scrollParentRef.current;
      if (!t) return;

      stopActiveCorrection();

      flushSync(() => {
        setPresentedData(data);
        feedRef.current?.prepend((p) => p + prependedCount);
      });

      const correct = () => {
        if (unmountedRef.current) return;
        const tNow = scrollParentRef.current;
        if (!tNow) return;

        const thr = compactFeedRef.current
          ? COMPACT_FEED_SCROLL_LOCK_DELTA
          : SCROLL_LOCK_DELTA;
        if (getFeedScrollOffset(tNow) <= thr && !lockFeedPositionRef.current) {
          stopActiveCorrection();
          return;
        }

        const el = findAnchorEl(tNow, anchor);
        if (!el) return;

        const delta = el.getBoundingClientRect().top - anchor.topBefore;
        if (Math.abs(delta) < 0.5) return;

        tNow.scrollTop += delta;
      };

      activeCorrectionRef.current = correct;
      activeCorrectionDeadlineRef.current =
        performance.now() + PREPEND_OBSERVE_MS;

      correct();

      requestAnimationFrame(() => {
        correct();
        requestAnimationFrame(correct);
      });

      const listEl = t.querySelector<HTMLElement>(
        '[data-testid="virtuoso-item-list"]',
      );
      if (listEl && typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(correct);
        observer.observe(listEl);
        resizeObserverRef.current = observer;
        resizeTimeoutRef.current = window.setTimeout(
          stopActiveCorrection,
          PREPEND_OBSERVE_MS,
        );
      }
    });
  }, [data, feedRef]);

  return { presentedData, handleTotalListHeightChanged };
};
